import { CreateUserDto } from "../../core/models/dtos/CreateUserDto";
import { UpdateUserDto } from "../../core/models/dtos/UpdateUserDto";
import { useApiClient } from "../clients/ApiClient";
import { mapFromCreateUserDto } from "../models/mappers/mapUser";
import { User } from "../../core/models/entities/User";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";

export interface UserRepository {
  createUser: (dto: CreateUserDto) => Promise<void>;
  deleteUser: (userId: number) => Promise<void>;
  /**
   * Updates name/phone of a user. The API (PUT /users) writes email and
   * hasLoggedIn unconditionally, so the full current user is required to
   * round-trip the fields this screen does not edit.
   */
  updateUser: (user: User, orgId: number, dto: UpdateUserDto) => Promise<void>;
  /** Per-user price visibility within an organization. */
  setUserHidePrices: (orgId: number, userId: number, hidePrices: boolean) => Promise<void>;
  useUsersOfOrganization: (orgId: number, options?: { enabled?: boolean }) => Resource<User[]>;
}

export function useUserRepository(): UserRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  async function invalidateQueries(orgId: number) {
    await queryClient.refetchQueries({
      queryKey: ["users", orgId],
      type: "all",
      refetchPage: () => true,
    });
  }

  async function createUser(dto: CreateUserDto): Promise<void> {
    await apiClient.createUser(mapFromCreateUserDto(dto));
    invalidateQueries(dto.organizationId).then();
  }

  async function deleteUser(userId: number): Promise<void> {
    await apiClient.deleteUser(userId);
  }

  async function updateUser(
    user: User,
    orgId: number,
    dto: UpdateUserDto
  ): Promise<void> {
    await apiClient.updateUser({
      id: user.id,
      organizationId: user.organizationId ?? orgId,
      // Sent back unchanged: the API overwrites these on every PUT.
      email: user.email,
      hasLoggedIn: user.hasLoggedIn ?? false,
      firstName: dto.firstName ?? user.firstName ?? "",
      lastName: dto.lastName ?? user.lastName ?? "",
      phone: dto.phone ?? user.phone,
    });
    invalidateQueries(orgId).then();
  }

  async function setUserHidePrices(
    orgId: number,
    userId: number,
    hidePrices: boolean
  ): Promise<void> {
    await apiClient.setUserHidePrices(orgId, userId, { hidePrices });
    invalidateQueries(orgId).then();
  }

  function useUsersOfOrganization(orgId: number, options?: { enabled?: boolean }): Resource<User[]> {
    return useQuery(["users", orgId], () =>
      apiClient.getUsersOfOrganization(orgId).then(),
      { enabled: options?.enabled ?? true }
    );
  }

  return {
    createUser,
    deleteUser,
    updateUser,
    setUserHidePrices,
    useUsersOfOrganization,
  };
}
